import {
  AlertTriangle,
  ArrowUpRight,
  Ban,
  Bell,
  CalendarClock,
  CheckCircle2,
  CheckSquare,
  Copy,
  Download,
  FileDown,
  FileStack,
  FileText,
  FileUp,
  Flag,
  Globe,
  Inbox,
  Layers,
  LayoutGrid,
  ListChecks,
  Loader2,
  type LucideIcon,
  Megaphone,
  ShieldAlert,
  Sparkles,
  StickyNote,
  Tags,
  Target,
  Trophy,
  Undo2,
  WandSparkles,
} from "lucide-react";

import type {
  AsyncTask,
  AsyncTaskTypeId,
} from "@/modern/components/async-tasks/types";
import {
  boardExportFallbackFileName,
  downloadBoardExportResult,
} from "@/modern/components/async-tasks/utils/boardExportDownload";
import { ALERT_TYPES, ASYNC_TASK_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { IMPORT_TYPES_HISTORY } from "@/other/constants/OtherKeys.jsx";
import { downloadFileFromData, showToast } from "@/other/utils/utils";
import { toApiCategoryKey, toUiCategoryKey } from "../utils/askUiContext";
import type {
  MappedNotificationCounts,
  NavigateTarget,
  Notification,
  NotificationActionDescriptor,
  NotificationCategoryGroup,
  NotificationConfigContext,
  NotificationCountsResponse,
  NotificationFilter,
  NotificationFilterOption,
  NotificationFilterType,
  NotificationListParams,
  NotificationMappedCategoryCount,
  NotificationMappedStatusCount,
  NotificationNotifyType,
  NotificationOpenTarget,
  NotificationTaskResult,
  NotificationTypeEntry,
} from "../types";

// ─── Private Helpers ───────────────────────────────────────────────────────────

type UnknownRecord = Record<string, unknown>;
type Awaitable<T> = T | Promise<T>;

function meta(notification: Notification): UnknownRecord {
  return (notification.async_task?.meta_data ?? {}) as UnknownRecord;
}

function stringMeta(notification: Notification, key: string): string | undefined {
  const value = meta(notification)[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function numberMeta(notification: Notification, key: string): number | undefined {
  const value = meta(notification)[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function recordLabel(record: unknown): string | undefined {
  if (!record || typeof record !== "object") return undefined;
  const value = record as { name?: string | null; title?: string | null; displayName?: string | null };
  return value.name?.trim() || value.title?.trim() || value.displayName?.trim() || undefined;
}

function resolveLabel(
  context: NotificationConfigContext,
  kind: Parameters<NonNullable<NotificationConfigContext["resolveEntityLabel"]>>[0],
  id: number | undefined,
): string | undefined {
  if (id == null) return undefined;
  const explicit = context.resolveEntityLabel?.(kind, id);
  if (explicit) return explicit;
  if (kind === "board") {
    return recordLabel(context.boardManager?.boardNameStore?.getById?.(id));
  }
  const storeKey = {
    "work-file": STORE_KEYS.marketingWorkfileStore,
    "marketing-page": STORE_KEYS.marketingPageStore,
    incentive: STORE_KEYS.outcomeIncentiveStore,
    context: STORE_KEYS.boardContextStore,
  }[kind];
  const fromCrud = recordLabel(context.project?.getCrudStore?.(storeKey)?.getById?.(id));
  if (fromCrud) return fromCrud;
  return kind === "incentive"
    ? recordLabel(context.project?.outcomeIncentiveStore?.getById?.(id))
    : undefined;
}

function taskStatus(notification: Notification) {
  return notification.async_task?.status ?? null;
}

function actorName(
  notification: Notification,
  context: NotificationConfigContext,
): string | null {
  const embedded = notification.async_task?.creator_name?.trim();
  if (embedded) return embedded;
  const creator = notification.async_task?.creator ?? notification.creator;
  if (creator == null) return null;
  return context.resolveUserName?.(creator) ?? null;
}

function boardName(notification: Notification): string {
  return stringMeta(notification, "board_name") ?? "board";
}

const DATA_TYPE_LABELS: Record<number, string> = {
  [IMPORT_TYPES_HISTORY.tasks]: "tasks",
  [IMPORT_TYPES_HISTORY.resources]: "resources",
  [IMPORT_TYPES_HISTORY.clients]: "clients",
  [IMPORT_TYPES_HISTORY.contacts]: "contacts",
  [IMPORT_TYPES_HISTORY["tasks-budget"]]: "task budgets",
  [IMPORT_TYPES_HISTORY["board-users"]]: "board users",
};

function dataTypeLabelFromMeta(record: UnknownRecord): string {
  const type = typeof record.data_type === "number" ? record.data_type : undefined;
  return type == null ? "data" : (DATA_TYPE_LABELS[type] ?? "data");
}

function fileOrDataLabelFromTask(task: AsyncTask): string {
  const record = task.meta_data as UnknownRecord;
  return typeof record.file_name === "string" && record.file_name.trim()
    ? record.file_name
    : dataTypeLabelFromMeta(record);
}

function toDownloadBlob(result: NotificationTaskResult, mimeType: string): Blob | null {
  if (result == null) return null;
  if (typeof Blob !== "undefined" && result instanceof Blob) return result;
  if (typeof result === "object" && "download_url" in result) return null;
  if (typeof result === "string") return new Blob([result], { type: mimeType });
  return new Blob([JSON.stringify(result, null, 2)], { type: mimeType });
}

async function downloadCompletedBoardExport(notification: Notification): Promise<void> {
  const task = notification.async_task;
  if (!task || task.status !== "Completed") return;
  try {
    await downloadBoardExportResult(
      task.id,
      boardExportFallbackFileName(stringMeta(notification, "board_name")),
    );
  } catch {
    showToast({
      msg: "Could not download the board export.",
      type: "error",
      duration: 3000,
    });
  }
}

async function downloadResultFile(
  notification: Notification,
  context: NotificationConfigContext,
  fileName: string,
  mimeType: string,
): Promise<void> {
  const task = notification.async_task;
  if (!task) return;
  const result = await context.getResult(task.id);
  if (context.downloadResult) {
    await context.downloadResult(notification, result, { fileName, mimeType });
    return;
  }
  if (typeof result === "object" && "download_url" in result) {
    const url = result.download_url;
    if (typeof url === "string") {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = typeof result.file_name === "string" ? result.file_name : fileName;
      anchor.click();
      return;
    }
  }
  const blob = toDownloadBlob(result, mimeType);
  if (blob) downloadFileFromData(blob, fileName);
}

async function openSiteUrlFromResult(
  notification: Notification,
  context: NotificationConfigContext,
  urlKey: "preview_url" | "publish_url",
): Promise<void> {
  const task = notification.async_task;
  if (!task || task.status === "Pending") return;
  try {
    const result = await context.getResult(task.id);
    const href = (result as UnknownRecord | null)?.[urlKey];
    if (typeof href === "string" && href.trim()) {
      window.open(href.trim(), "_blank", "noopener,noreferrer");
    }
  } catch {
    showToast({ msg: "Could not open the published site.", type: "error", duration: 3000 });
  }
}

async function runOpenAction(
  notification: Notification,
  context: NotificationConfigContext,
  action: () => Awaitable<unknown>,
): Promise<void> {
  if (taskStatus(notification) !== "Pending" && !notification.is_seen) {
    await context.markSeen(notification);
  }
  context.closePopup();
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  await action();
}

function openAction(
  notification: Notification,
  context: NotificationConfigContext,
  options: { label: string; icon?: LucideIcon; run?: () => Awaitable<unknown>; target?: NavigateTarget },
): NotificationActionDescriptor {
  return {
    key: "open",
    label: options.label,
    icon: options.icon ?? ArrowUpRight,
    variant: "outline",
    onClick: () =>
      runOpenAction(notification, context, () => {
        if (options.run) return options.run();
        return options.target ? context.navigate?.(options.target) : undefined;
      }),
  };
}

function cancelAction(
  notification: Notification,
  context: NotificationConfigContext,
): NotificationActionDescriptor | null {
  const task = notification.async_task;
  if (!task || task.status !== "Pending") return null;
  const currentUserId = context.currentUser?.id;
  const creatorAllowed = currentUserId == null || task.creator == null || task.creator === currentUserId;
  const permissionAllowed = context.permissions?.canCancelTask?.(notification) ?? true;
  const canCancel = creatorAllowed && permissionAllowed;
  return {
    key: "cancel",
    label: "Cancel task",
    icon: Ban,
    variant: "destructive",
    disabled: !canCancel,
    disabledReason: !creatorAllowed
      ? "Only the task creator can cancel"
      : !permissionAllowed
        ? "You do not have permission to cancel this task"
        : undefined,
    onClick: async () => {
      if (canCancel) await context.cancelTask(task.id);
    },
  };
}

function asyncStatusMessage(
  notification: Notification,
  messages: {
    getPendingMessage?: (task: AsyncTask) => string;
    getCompletedMessage?: (task: AsyncTask) => string;
    getResolvedMessage?: (task: AsyncTask) => string;
    getFailedMessage?: (task: AsyncTask) => string;
  },
): string {
  const task = notification.async_task;
  if (!task) return notification.description ?? "";
  switch (task.status) {
    case "Pending": return messages.getPendingMessage?.(task) ?? "Working…";
    case "Completed": return messages.getCompletedMessage?.(task) ?? "Completed";
    case "Resolved": return messages.getResolvedMessage?.(task) ?? messages.getCompletedMessage?.(task) ?? "Resolved";
    case "Failed": return messages.getFailedMessage?.(task) ?? "Failed";
    case "Cancelled": return "Cancelled";
  }
}

function asyncGetActions(
  notification: Notification,
  context: NotificationConfigContext,
  opts: {
    label: string;
    getNavigateTarget?: (notification: Notification) => NavigateTarget | null;
    primaryActionLabel?: string;
    primaryActionIcon?: LucideIcon;
    runPrimaryAction?: (notification: Notification, context: NotificationConfigContext) => Awaitable<unknown>;
    completionMode?: "auto" | "user";
  },
): NotificationActionDescriptor[] {
  const task = notification.async_task;
  if (!task) return [];
  const actions: NotificationActionDescriptor[] = [];
  const target = opts.getNavigateTarget?.(notification) ?? null;
  if (
    task.status !== "Failed" &&
    task.status !== "Cancelled" &&
    task.status !== "Resolved" &&
    (target || opts.runPrimaryAction)
  ) {
    actions.push(
      openAction(notification, context, {
        label: task.status === "Pending"
          ? `Go to ${opts.label}`
          : (opts.primaryActionLabel ?? "View"),
        icon: task.status === "Pending" ? ArrowUpRight : (opts.primaryActionIcon ?? ArrowUpRight),
        run: opts.runPrimaryAction
          ? () => opts.runPrimaryAction?.(notification, context)
          : async () => {
              if (task.status === "Completed" && opts.completionMode === "auto") {
                try { await context.getResult(task.id); } catch { /* already retrieved */ }
              }
              if (target) await context.navigate?.(target);
            },
      }),
    );
  }
  const cancel = cancelAction(notification, context);
  if (cancel) actions.push(cancel);
  return actions;
}

function defaultAlertTitle(notification: Notification, label: string): string {
  const title = notification.title?.trim();
  return title ? `${label}: ${title}` : label;
}

function defaultAlertDescription(notification: Notification, label: string): string {
  const tag = notification.alert_tag === "Board Alert"
    ? "Alert Description"
    : (notification.alert_tag ?? label);
  return notification.description ? `${tag}: ${notification.description}` : tag;
}

function alertGetActions(
  notification: Notification,
  context: NotificationConfigContext,
  opts: { primaryActionLabel?: string; icon?: LucideIcon; openTarget?: (n: Notification) => NotificationOpenTarget },
): NotificationActionDescriptor[] {
  if (!opts.openTarget || !opts.primaryActionLabel) return [];
  return [
    openAction(notification, context, {
      label: opts.primaryActionLabel,
      icon: opts.icon,
      run: () => context.openNotification?.(notification, opts.openTarget?.(notification) ?? { kind: "entity" }),
    }),
  ];
}

function withBoard(notification: Notification, value: string): string {
  const name = stringMeta(notification, "board_name");
  return name ? `${value} for ${name}` : value;
}

const MY_SWIVU_TARGET: NavigateTarget = {
  viewType: VIEW_TYPES.mySwivu,
  viewName: "My Swivu",
};

/** Refresh board list and select the board named in async meta; fall back to My Swivu. */
async function openBoardFromMetaByName(
  notification: Notification,
  context: NotificationConfigContext,
): Promise<void> {
  const status = taskStatus(notification);

  if (status === "Pending") {
    await context.navigate?.(MY_SWIVU_TARGET);
    return;
  }

  const name = stringMeta(notification, "board_name");
  const boardManager = context.boardManager;
  const task = notification.async_task;

  if (status === "Completed" && task) {
    try {
      await context.getResult(task.id);
    } catch {
      /* already retrieved */
    }
  }

  if (!name || !boardManager?.changeSelectedBoard) {
    await context.navigate?.(MY_SWIVU_TARGET);
    return;
  }

  try {
    await boardManager.getRefreshUserBoardsListing?.();
  } catch {
    /* still try current store */
  }

  const store = boardManager.boardNameStore;
  const board =
    typeof store?.find === "function"
      ? store.find((record: { name?: string }) => record.name === name)
      : store?.records?.find?.((record: { name?: string }) => record.name === name);

  if (board) {
    boardManager.changeSelectedBoard(board);
    return;
  }

  await context.navigate?.(MY_SWIVU_TARGET);
}

// ─── Config Entries ────────────────────────────────────────────────────────────

export const NOTIFICATION_CONFIG: NotificationTypeEntry[] = [
  // ── Async Core ──
  {
    type: ASYNC_TASK_TYPES.BOARD_DUPLICATION,
    notifyType: "async",
    label: "Board duplication",
    icon: Copy,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "copy",
    categoryGroup: "task",
    classes: { bg: "bg-clay", text: "text-clay-foreground", ring: "ring-clay/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_DUPLICATION },
    getTitle: (n) =>
      taskStatus(n) === "Pending" ? `Duplicating ${boardName(n)}`
        : taskStatus(n) === "Completed" || taskStatus(n) === "Resolved" ? `Duplicated ${boardName(n)}`
          : "Board duplication",
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => `Duplicating "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
      getCompletedMessage: (t) => `"${(t.meta_data as UnknownRecord).board_name ?? "board"}" duplicated`,
      getFailedMessage: (t) => `Failed to duplicate "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
    }),
    getStatusLabel: (n) => {
      switch (taskStatus(n)) {
        case "Pending": return "In progress";
        case "Completed": case "Resolved": return "Copy";
        case "Failed": return "Failed";
        case "Cancelled": return "Cancelled";
        default: return "Copy";
      }
    },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_DUPLICATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Board duplication",
      primaryActionLabel: "Open board",
      primaryActionIcon: LayoutGrid,
      runPrimaryAction: openBoardFromMetaByName,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openBoardFromMetaByName(n, ctx));
    },
    getPendingMessage: (t) => `Duplicating "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
    getCompletedMessage: (t) => `"${(t.meta_data as UnknownRecord).board_name ?? "board"}" duplicated`,
    getFailedMessage: (t) => `Failed to duplicate "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
  },
  {
    type: ASYNC_TASK_TYPES.CSV_IMPORT,
    notifyType: "async",
    label: "CSV import",
    icon: FileUp,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "imports",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.CSV_IMPORT },
    getTitle: (n) => {
      const file = stringMeta(n, "file_name");
      return `CSV Import${file ? `: ${file}` : `: ${dataTypeLabelFromMeta(meta(n))}`}`;
    },
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => `Importing ${fileOrDataLabelFromTask(t)}…`,
      getCompletedMessage: (t) => `${fileOrDataLabelFromTask(t)} import complete`,
      getFailedMessage: (t) => `${fileOrDataLabelFromTask(t)} import failed`,
    }),
    getStatusLabel: (n) => {
      switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; }
    },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.CSV_IMPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, { label: "CSV import", completionMode: "auto" }),
    onOpen: () => {},
    getPendingMessage: (t) => `Importing ${fileOrDataLabelFromTask(t)}…`,
    getCompletedMessage: (t) => `${fileOrDataLabelFromTask(t)} import complete`,
    getFailedMessage: (t) => `${fileOrDataLabelFromTask(t)} import failed`,
  },
  {
    type: ASYNC_TASK_TYPES.CSV_EXPORT,
    notifyType: "async",
    label: "CSV export",
    icon: FileDown,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "imports",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.CSV_EXPORT },
    getTitle: (n) => `CSV Export: ${dataTypeLabelFromMeta(meta(n))}`,
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => `Exporting ${fileOrDataLabelFromTask(t)}…`,
      getCompletedMessage: (t) => `${fileOrDataLabelFromTask(t)} export ready`,
      getFailedMessage: (t) => `${fileOrDataLabelFromTask(t)} export failed`,
    }),
    getStatusLabel: (n) => {
      switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; }
    },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.CSV_EXPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "CSV export",
      primaryActionLabel: "Download CSV",
      primaryActionIcon: FileDown,
      runPrimaryAction: (notification, context) =>
        downloadResultFile(notification, context, stringMeta(notification, "file_name") ?? "export.csv", "text/csv"),
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () =>
        downloadResultFile(n, ctx, stringMeta(n, "file_name") ?? "export.csv", "text/csv"));
    },
    getPendingMessage: (t) => `Exporting ${fileOrDataLabelFromTask(t)}…`,
    getCompletedMessage: (t) => `${fileOrDataLabelFromTask(t)} export ready`,
    getFailedMessage: (t) => `${fileOrDataLabelFromTask(t)} export failed`,
  },
  {
    type: ASYNC_TASK_TYPES.UNDO_CSV_IMPORT,
    notifyType: "async",
    label: "Undo import",
    icon: Undo2,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "imports",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.UNDO_CSV_IMPORT },
    getTitle: (n) => `Undo Import: ${dataTypeLabelFromMeta(meta(n))}`,
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Undoing import…", getCompletedMessage: () => "Import undo complete", getFailedMessage: () => "Import undo failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.UNDO_CSV_IMPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, { label: "Undo import", completionMode: "auto" }),
    onOpen: () => {},
    getPendingMessage: () => "Undoing import…",
    getCompletedMessage: () => "Import undo complete",
    getFailedMessage: () => "Import undo failed",
  },
  {
    type: ASYNC_TASK_TYPES.DEMO_BOARD_CREATION,
    notifyType: "async",
    label: "Test drive",
    icon: WandSparkles,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "board",
    categoryGroup: "task",
    classes: { bg: "bg-action", text: "text-action-foreground", ring: "ring-action/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.DEMO_BOARD_CREATION },
    getTitle: (n) => boardName(n) === "board" ? "Test Drive Generation" : `Test Drive Generation: ${boardName(n)}`,
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => `Creating test drive "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
      getCompletedMessage: (t) => `Test drive "${(t.meta_data as UnknownRecord).board_name ?? "board"}" ready`,
      getFailedMessage: (t) => `Failed to create test drive "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Board"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Board"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.DEMO_BOARD_CREATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Test drive",
      primaryActionLabel: "Open board",
      primaryActionIcon: LayoutGrid,
      runPrimaryAction: openBoardFromMetaByName,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openBoardFromMetaByName(n, ctx));
    },
    getPendingMessage: (t) => `Creating test drive "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
    getCompletedMessage: (t) => `Test drive "${(t.meta_data as UnknownRecord).board_name ?? "board"}" ready`,
    getFailedMessage: (t) => `Failed to create test drive "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_IMPORT,
    notifyType: "async",
    label: "Board import",
    icon: FileUp,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "imports",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_IMPORT },
    getTitle: (n) => boardName(n) === "board" ? "Import" : `Import: ${boardName(n)}`,
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => `Importing "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
      getCompletedMessage: (t) => `"${(t.meta_data as UnknownRecord).board_name ?? "board"}" imported`,
      getFailedMessage: (t) => `Failed to import "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_IMPORT}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Board import",
      primaryActionLabel: "Open import",
      primaryActionIcon: FileUp,
      runPrimaryAction: openBoardFromMetaByName,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openBoardFromMetaByName(n, ctx));
    },
    getPendingMessage: (t) => `Importing "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
    getCompletedMessage: (t) => `"${(t.meta_data as UnknownRecord).board_name ?? "board"}" imported`,
    getFailedMessage: (t) => `Failed to import "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_EXPORT,
    notifyType: "async",
    label: "Board export",
    icon: Download,
    stream: "system",
    scope: "account",
    completionMode: "auto",
    categoryKey: "imports",
    categoryGroup: "task",
    classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_EXPORT },
    getTitle: (n) => boardName(n) === "board" ? "Export" : `Export: ${boardName(n)}`,
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => `Exporting "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
      getCompletedMessage: (t) => `"${(t.meta_data as UnknownRecord).board_name ?? "board"}" export ready`,
      getFailedMessage: (t) => `Failed to export "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Import"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Import"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_EXPORT}`,
    getActions: (n, ctx) => {
      const actions: NotificationActionDescriptor[] = [];
      if (taskStatus(n) === "Completed" && n.async_task) {
        actions.push(
          openAction(n, ctx, {
            label: "Download export",
            icon: Download,
            run: () => downloadCompletedBoardExport(n),
          }),
        );
      }
      const cancel = cancelAction(n, ctx);
      if (cancel) actions.push(cancel);
      return actions;
    },
    onOpen: (n, ctx) => {
      if (taskStatus(n) !== "Completed" || n.async_task == null) return;
      void runOpenAction(n, ctx, () => downloadCompletedBoardExport(n));
    },
    getPendingMessage: (t) => `Exporting "${(t.meta_data as UnknownRecord).board_name ?? "board"}"…`,
    getCompletedMessage: (t) => `"${(t.meta_data as UnknownRecord).board_name ?? "board"}" export ready`,
    getFailedMessage: (t) => `Failed to export "${(t.meta_data as UnknownRecord).board_name ?? "board"}"`,
  },
  {
    type: ASYNC_TASK_TYPES.INCENTIVE_EVALUATION,
    notifyType: "async",
    label: "Incentive evaluation",
    icon: Trophy,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "incentive",
    categoryGroup: "task",
    classes: { bg: "bg-ochre", text: "text-ochre-foreground", ring: "ring-ochre/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.INCENTIVE_EVALUATION },
    getTitle: (n, ctx) => {
      const name = resolveLabel(ctx, "incentive", numberMeta(n, "incentive_id")) ?? stringMeta(n, "incentive_name");
      return name ? `Incentive Evaluation: ${name}` : "Incentive Evaluation";
    },
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Evaluating outcome points…", getCompletedMessage: () => "Outcome points evaluated", getFailedMessage: () => "Incentive evaluation failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Incentive"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Incentive"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.INCENTIVE_EVALUATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Incentive evaluation",
      primaryActionLabel: "View outcomes",
      primaryActionIcon: Trophy,
      runPrimaryAction: async (notification, context) => {
        const task = notification.async_task;
        if (!task) return;
        const result = await context.getResult(task.id);
        await context.openIncentiveResults?.(notification, result);
      },
      completionMode: "user",
    }),
    onOpen: async (n, ctx) => {
      const task = n.async_task;
      if (!task) return;
      await runOpenAction(n, ctx, async () => {
        const result = await ctx.getResult(task.id);
        await ctx.openIncentiveResults?.(n, result);
      });
    },
    getPendingMessage: () => "Evaluating outcome points…",
    getCompletedMessage: () => "Outcome points evaluated",
    getFailedMessage: () => "Incentive evaluation failed",
  },
  {
    type: ASYNC_TASK_TYPES.BULK_ENTITY_COPY,
    notifyType: "async",
    label: "Bulk copy",
    icon: Copy,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "copy",
    categoryGroup: "task",
    classes: { bg: "bg-clay", text: "text-clay-foreground", ring: "ring-clay/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BULK_ENTITY_COPY },
    getTitle: (n) => {
      const count = numberMeta(n, "entity_count");
      const type = stringMeta(n, "entity_type") ?? "entities";
      return count && count > 0 ? `Copy ${count} ${type}` : "Bulk copy";
    },
    getDescription: (n, ctx) => {
      const base = n.async_task
        ? asyncStatusMessage(n, {
            getPendingMessage: (t) => { const r = t.meta_data as UnknownRecord; const c = typeof r.entity_count === "number" ? r.entity_count : undefined; return c && c > 0 ? `Copying ${c} ${r.entity_type ?? "entities"}…` : `Copying ${r.entity_type ?? "entities"}…`; },
            getCompletedMessage: (t) => `${(t.meta_data as UnknownRecord).entity_type ?? "entities"} copied successfully`,
            getFailedMessage: (t) => `Failed to copy ${(t.meta_data as UnknownRecord).entity_type ?? "entities"}`,
          })
        : "Bulk copy";
      const destination = resolveLabel(ctx, "board", numberMeta(n, "destination_board_id"));
      return destination ? `${base} → ${destination}` : base;
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Copy"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Copy"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BULK_ENTITY_COPY}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, { label: "Bulk copy", completionMode: "user" }),
    onOpen: () => {},
    getPendingMessage: (t) => { const r = t.meta_data as UnknownRecord; const c = typeof r.entity_count === "number" ? r.entity_count : undefined; return c && c > 0 ? `Copying ${c} ${r.entity_type ?? "entities"}…` : `Copying ${r.entity_type ?? "entities"}…`; },
    getCompletedMessage: (t) => `${(t.meta_data as UnknownRecord).entity_type ?? "entities"} copied successfully`,
    getFailedMessage: (t) => `Failed to copy ${(t.meta_data as UnknownRecord).entity_type ?? "entities"}`,
  },
  // ── Async Marketing ──
  {
    type: ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION,
    notifyType: "async",
    label: "Marketing manifest",
    icon: FileText,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION },
    getTitle: (n) => { const name = stringMeta(n, "board_name"); return name ? `Marketing manifest: ${name}` : "Marketing manifest"; },
    getDescription: (n) => withBoard(n, asyncStatusMessage(n, { getPendingMessage: () => "Generating marketing manifest…", getCompletedMessage: () => "Marketing manifest ready", getFailedMessage: () => "Manifest generation failed" })),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_MANIFEST_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Marketing manifest",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: numberMeta(notification, "board_context_id") != null ? { contextId: numberMeta(notification, "board_context_id") } : {} }),
      primaryActionLabel: "Open manifest",
      primaryActionIcon: FileText,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: numberMeta(n, "board_context_id") != null ? { contextId: numberMeta(n, "board_context_id") } : {} })); },
    getPendingMessage: () => "Generating marketing manifest…",
    getCompletedMessage: () => "Marketing manifest ready",
    getFailedMessage: () => "Manifest generation failed",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION,
    notifyType: "async",
    label: "Marketing tags",
    icon: Tags,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION },
    getTitle: (n) => { const name = stringMeta(n, "board_name"); return name ? `Marketing tags: ${name}` : "Marketing tags"; },
    getDescription: (n) => withBoard(n, asyncStatusMessage(n, { getPendingMessage: () => "Generating marketing tags…", getCompletedMessage: () => "Marketing tags updated", getFailedMessage: () => "Tags generation failed" })),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_TAGS_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Marketing tags",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: { openTagReview: true, ...(numberMeta(notification, "board_context_id") != null ? { contextId: numberMeta(notification, "board_context_id") } : {}) } }),
      primaryActionLabel: "View tags",
      primaryActionIcon: Tags,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.marketingManifest, viewName: "Marketing Manifest", otherParams: { openTagReview: true, ...(numberMeta(n, "board_context_id") != null ? { contextId: numberMeta(n, "board_context_id") } : {}) } })); },
    getPendingMessage: () => "Generating marketing tags…",
    getCompletedMessage: () => "Marketing tags updated",
    getFailedMessage: () => "Tags generation failed",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION,
    notifyType: "async",
    label: "Work file",
    icon: FileStack,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION },
    getTitle: (n, ctx) => {
      const name = resolveLabel(ctx, "work-file", numberMeta(n, "work_file_id")) ?? stringMeta(n, "name");
      return name ? `Workfile Generation: ${name}` : "Workfile Generation";
    },
    getDescription: (n, ctx) => {
      const name = resolveLabel(ctx, "work-file", numberMeta(n, "work_file_id")) ?? stringMeta(n, "name");
      if (!name) return asyncStatusMessage(n, { getPendingMessage: () => "Generating marketing work file…", getCompletedMessage: () => "Work file ready", getFailedMessage: () => "Work file generation failed" });
      if (taskStatus(n) === "Pending") return `Generating "${name}"…`;
      if (taskStatus(n) === "Failed") return `Failed to generate "${name}"`;
      return `"${name}" is ready`;
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Work file",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(notification, "work_file_id") != null ? { workFileId: numberMeta(notification, "work_file_id") } : {} }),
      primaryActionLabel: "Open work file",
      primaryActionIcon: FileStack,
      completionMode: "user",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(n, "work_file_id") != null ? { workFileId: numberMeta(n, "work_file_id") } : {} })); },
    getPendingMessage: () => "Generating marketing work file…",
    getCompletedMessage: () => "Work file ready",
    getFailedMessage: () => "Work file generation failed",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION,
    notifyType: "async",
    label: "Page generation",
    icon: Globe,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION },
    getTitle: (n, ctx) => {
      const name = resolveLabel(ctx, "marketing-page", numberMeta(n, "marketing_page_id"));
      return name ? `Page Generation: ${name}` : "Page Generation";
    },
    getDescription: (n, ctx) => {
      const workFileId = numberMeta(n, "work_file_id");
      const name = resolveLabel(ctx, "work-file", workFileId) ?? (workFileId != null ? `work file #${workFileId}` : undefined);
      if (!name) return asyncStatusMessage(n, { getPendingMessage: () => "Publishing marketing page…", getCompletedMessage: () => "Marketing page published", getFailedMessage: () => "Page publish failed" });
      if (taskStatus(n) === "Pending") return `Publishing page from "${name}"…`;
      if (taskStatus(n) === "Failed") return `Failed to publish page from "${name}"`;
      return `Page published from "${name}"`;
    },
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_PAGE_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Page generation",
      getNavigateTarget: (notification) =>
        numberMeta(notification, "marketing_page_id") != null
          ? { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages", otherParams: { marketingPageId: numberMeta(notification, "marketing_page_id") } }
          : numberMeta(notification, "work_file_id") != null
            ? { viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: { workFileId: numberMeta(notification, "work_file_id") } }
            : { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages" },
      primaryActionLabel: "Open page",
      primaryActionIcon: ArrowUpRight,
      completionMode: "user",
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => {
        const target = numberMeta(n, "marketing_page_id") != null
          ? { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages", otherParams: { marketingPageId: numberMeta(n, "marketing_page_id") } }
          : numberMeta(n, "work_file_id") != null
            ? { viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: { workFileId: numberMeta(n, "work_file_id") } }
            : { viewType: VIEW_TYPES.marketingPages, viewName: "Marketing Pages" };
        return ctx.navigate?.(target);
      });
    },
    getPendingMessage: () => "Publishing marketing page…",
    getCompletedMessage: () => "Marketing page published",
    getFailedMessage: () => "Page publish failed",
  },
  {
    type: ASYNC_TASK_TYPES.SITE_PUBLISH,
    notifyType: "async",
    label: "Site publish",
    icon: Globe,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.SITE_PUBLISH },
    getTitle: (n) => { const name = stringMeta(n, "board_name"); return name ? `Site publish: ${name}` : "Site publish"; },
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Publishing site…", getCompletedMessage: () => "Site published", getFailedMessage: () => "Site publish failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.SITE_PUBLISH}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Site publish",
      primaryActionLabel: "View site",
      primaryActionIcon: Globe,
      completionMode: "auto",
      ...(taskStatus(n) !== "Pending"
        ? { runPrimaryAction: (notification, context) => openSiteUrlFromResult(notification, context, "preview_url") }
        : {}),
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openSiteUrlFromResult(n, ctx, "preview_url"));
    },
    getPendingMessage: () => "Publishing site…",
    getCompletedMessage: () => "Site published",
    getFailedMessage: () => "Site publish failed",
  },
  {
    type: ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY,
    notifyType: "async",
    label: "Custom domain deploy",
    icon: Globe,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY },
    getTitle: (n) => { const name = stringMeta(n, "board_name"); return name ? `Custom domain: ${name}` : "Custom domain deploy"; },
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Deploying to custom domain…", getCompletedMessage: () => "Custom domain deploy complete", getFailedMessage: () => "Custom domain deploy failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.SITE_CUSTOM_DOMAIN_DEPLOY}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Custom domain deploy",
      primaryActionLabel: "View site",
      primaryActionIcon: Globe,
      completionMode: "auto",
      ...(taskStatus(n) !== "Pending"
        ? { runPrimaryAction: (notification, context) => openSiteUrlFromResult(notification, context, "publish_url") }
        : {}),
    }),
    onOpen: (n, ctx) => {
      runOpenAction(n, ctx, () => openSiteUrlFromResult(n, ctx, "publish_url"));
    },
    getPendingMessage: () => "Deploying to custom domain…",
    getCompletedMessage: () => "Custom domain deploy complete",
    getFailedMessage: () => "Custom domain deploy failed",
  },
  {
    type: ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION,
    notifyType: "async",
    label: "Document generation",
    icon: FileText,
    stream: "system",
    scope: "board",
    completionMode: "user",
    categoryKey: "marketing",
    categoryGroup: "task",
    classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION },
    getTitle: (n, ctx) => {
      const format = stringMeta(n, "format")?.toUpperCase();
      const prefix = format ? `${format} Generation` : "Document Generation";
      const name = resolveLabel(ctx, "work-file", numberMeta(n, "work_file_id"));
      return name ? `${prefix}: ${name}` : prefix;
    },
    getDescription: (n) => asyncStatusMessage(n, {
      getPendingMessage: (t) => { const r = t.meta_data as UnknownRecord; return typeof r.name === "string" && r.name.trim() ? `Generating "${r.name.trim()}"…` : typeof r.format === "string" ? `Generating ${r.format.toUpperCase()} document…` : "Generating document…"; },
      getCompletedMessage: (t) => { const r = t.meta_data as UnknownRecord; return typeof r.name === "string" && r.name.trim() ? `"${r.name.trim()}" is ready` : typeof r.format === "string" ? `${r.format.toUpperCase()} document ready` : "Document ready"; },
      getFailedMessage: (t) => { const r = t.meta_data as UnknownRecord; return typeof r.name === "string" && r.name.trim() ? `Failed to generate "${r.name.trim()}"` : typeof r.format === "string" ? `${r.format.toUpperCase()} generation failed` : "Document generation failed"; },
    }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Marketing"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Marketing"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.MARKETING_DOCUMENT_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Document generation",
      getNavigateTarget: (notification) => ({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(notification, "work_file_id") != null ? { workFileId: numberMeta(notification, "work_file_id") } : {} }),
      primaryActionLabel: "Open document",
      primaryActionIcon: FileText,
      completionMode: "user",
    }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.workFiles, viewName: "Work Files", otherParams: numberMeta(n, "work_file_id") != null ? { workFileId: numberMeta(n, "work_file_id") } : {} })); },
    getPendingMessage: (t) => { const r = t.meta_data as UnknownRecord; return typeof r.name === "string" && r.name.trim() ? `Generating "${r.name.trim()}"…` : typeof r.format === "string" ? `Generating ${r.format.toUpperCase()} document…` : "Generating document…"; },
    getCompletedMessage: (t) => { const r = t.meta_data as UnknownRecord; return typeof r.name === "string" && r.name.trim() ? `"${r.name.trim()}" is ready` : typeof r.format === "string" ? `${r.format.toUpperCase()} document ready` : "Document ready"; },
    getFailedMessage: (t) => { const r = t.meta_data as UnknownRecord; return typeof r.name === "string" && r.name.trim() ? `Failed to generate "${r.name.trim()}"` : typeof r.format === "string" ? `${r.format.toUpperCase()} generation failed` : "Document generation failed"; },
  },
  // ── Async Context ──
  {
    type: ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
    notifyType: "async",
    label: "Context questions",
    icon: Layers,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "context",
    categoryGroup: "task",
    classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION },
    getTitle: (n, ctx) => { const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id")); return name ? `Context Questions: ${name}` : "Context Questions"; },
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Generating context questions…", getCompletedMessage: () => "Context questions ready", getFailedMessage: () => "Question generation failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Context"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Context"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Context questions",
      getNavigateTarget: (notification) => numberMeta(notification, "board_context_id") == null ? null : { viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: numberMeta(notification, "board_context_id"), openWizard: true } },
      primaryActionLabel: "Open context",
      primaryActionIcon: Layers,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { const cId = numberMeta(n, "board_context_id"); if (cId != null) runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: cId, openWizard: true } })); },
    getPendingMessage: () => "Generating context questions…",
    getCompletedMessage: () => "Context questions ready",
    getFailedMessage: () => "Question generation failed",
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
    notifyType: "async",
    label: "Context build",
    icon: Layers,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "context",
    categoryGroup: "task",
    classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION },
    getTitle: (n, ctx) => { const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id")); return name ? `Context Generation: ${name}` : "Context Generation"; },
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Building board context…", getCompletedMessage: () => "Board context ready", getFailedMessage: () => "Context build failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Context"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Context"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Context build",
      getNavigateTarget: (notification) => numberMeta(notification, "board_context_id") == null ? null : { viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: numberMeta(notification, "board_context_id") } },
      primaryActionLabel: "Open context",
      primaryActionIcon: Layers,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { const cId = numberMeta(n, "board_context_id"); if (cId != null) runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: cId } })); },
    getPendingMessage: () => "Building board context…",
    getCompletedMessage: () => "Board context ready",
    getFailedMessage: () => "Context build failed",
  },
  {
    type: ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
    notifyType: "async",
    label: "Context suggestions",
    icon: Sparkles,
    stream: "system",
    scope: "board",
    completionMode: "auto",
    categoryKey: "context",
    categoryGroup: "task",
    classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" },
    filter: { notify_type: "async", type: ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION },
    getTitle: (n, ctx) => { const name = resolveLabel(ctx, "context", numberMeta(n, "board_context_id")); return name ? `Suggestions: ${name}` : "Suggestions"; },
    getDescription: (n) => asyncStatusMessage(n, { getPendingMessage: () => "Generating context suggestions…", getCompletedMessage: () => "Context suggestions ready", getFailedMessage: () => "Suggestion generation failed" }),
    getStatusLabel: (n) => { switch (taskStatus(n)) { case "Pending": return "In progress"; case "Completed": case "Resolved": return "Context"; case "Failed": return "Failed"; case "Cancelled": return "Cancelled"; default: return "Context"; } },
    getActorName: actorName,
    notableWhen: (n) => taskStatus(n) === "Failed" || taskStatus(n) === "Pending",
    aggregateKey: () => `async:${ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION}`,
    getActions: (n, ctx) => asyncGetActions(n, ctx, {
      label: "Context suggestions",
      getNavigateTarget: (notification) => numberMeta(notification, "board_context_id") == null ? null : { viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: numberMeta(notification, "board_context_id") } },
      primaryActionLabel: "Open context",
      primaryActionIcon: Sparkles,
      completionMode: "auto",
    }),
    onOpen: (n, ctx) => { const cId = numberMeta(n, "board_context_id"); if (cId != null) runOpenAction(n, ctx, () => ctx.navigate?.({ viewType: VIEW_TYPES.context, viewName: "Context", otherParams: { contextId: cId } })); },
    getPendingMessage: () => "Generating context suggestions…",
    getCompletedMessage: () => "Context suggestions ready",
    getFailedMessage: () => "Suggestion generation failed",
  },
  // ── Alerts ──
  {
    type: ALERT_TYPES.REMINDER, notifyType: "notification", label: "Reminder", icon: Bell, stream: "notification", categoryKey: "reminders", categoryGroup: "alert",
    classes: { bg: "bg-warning", text: "text-warning-foreground", ring: "ring-warning/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.REMINDER },
    getTitle: (n) => defaultAlertTitle(n, "Reminder"),
    getDescription: (n) => defaultAlertDescription(n, "Reminder"),
    getStatusLabel: () => "Reminders",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open reminder", icon: Bell, openTarget: () => ({ kind: "reminder-detail" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "reminder-detail" })); },
  },
  {
    type: ALERT_TYPES.Schedule, notifyType: "notification", label: "Schedule change", icon: CalendarClock, stream: "notification", categoryKey: "schedule", categoryGroup: "alert",
    classes: { bg: "bg-rose", text: "text-rose-foreground", ring: "ring-rose/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.Schedule },
    getTitle: (n) => defaultAlertTitle(n, "Schedule change"),
    getDescription: (n) => defaultAlertDescription(n, "Schedule change"),
    getStatusLabel: () => "Schedule",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open schedule", icon: CalendarClock, openTarget: () => ({ kind: "schedule" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "schedule" })); },
  },
  {
    type: ALERT_TYPES.Checklist, notifyType: "notification", label: "Checklist", icon: CheckSquare, stream: "notification", categoryKey: "nudges", categoryGroup: "alert",
    classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.Checklist },
    getTitle: (n) => n.title?.trim() ? `Checklist: ${n.title.trim()}` : "Checklist",
    getDescription: (n) => defaultAlertDescription(n, "Checklist"),
    getStatusLabel: () => "Nudges",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open Checklist", icon: CheckSquare, openTarget: (notification) => ({ kind: "checklist", checklistId: notification.checklist ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "checklist", checklistId: n.checklist ?? undefined })); },
  },
  {
    type: ALERT_TYPES.Nudge, notifyType: "notification", label: "Nudge", icon: ListChecks, stream: "notification", categoryKey: "nudges", categoryGroup: "alert",
    classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.Nudge },
    getTitle: (n) => n.title?.trim() ? `Checklist: ${n.title.trim()}` : "Checklist",
    getDescription: (n) => n.description?.trim() || "Checklist nudge",
    getStatusLabel: () => "Nudges",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open Checklist", icon: ListChecks, openTarget: (notification) => ({ kind: "checklist", checklistId: notification.checklist ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "checklist", checklistId: n.checklist ?? undefined })); },
  },
  {
    type: ALERT_TYPES.BoardAlert, notifyType: "notification", label: "Board alert", icon: ShieldAlert, stream: "notification", categoryKey: "board-alert", categoryGroup: "alert",
    classes: { bg: "bg-destructive", text: "text-destructive-foreground", ring: "ring-destructive/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.BoardAlert },
    getTitle: (n) => defaultAlertTitle(n, "Board alert"),
    getDescription: (n) => defaultAlertDescription(n, "Board alert"),
    getStatusLabel: () => "Board alert",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: () => [],
    onOpen: () => {},
  },
  {
    type: ALERT_TYPES.EntityStatus, notifyType: "notification", label: "Status", icon: Flag, stream: "notification", categoryKey: "status", categoryGroup: "alert",
    classes: { bg: "bg-slate", text: "text-slate-foreground", ring: "ring-slate/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.EntityStatus },
    getTitle: (n) => defaultAlertTitle(n, "Status"),
    getDescription: (n) => defaultAlertDescription(n, "Status"),
    getStatusLabel: () => "Status",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open", icon: Flag, openTarget: () => ({ kind: "entity" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "entity" })); },
  },
  {
    type: ALERT_TYPES.GENERAL, notifyType: "notification", label: "General", icon: Megaphone, stream: "notification", categoryKey: "general", categoryGroup: "alert",
    classes: { bg: "bg-chart-3", text: "text-white", ring: "ring-chart-3/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.GENERAL },
    getTitle: (n) => defaultAlertTitle(n, "General"),
    getDescription: (n) => defaultAlertDescription(n, "General"),
    getStatusLabel: () => "General",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open", icon: Megaphone, openTarget: () => ({ kind: "entity" }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "entity" })); },
  },
  {
    type: ALERT_TYPES.NOTE, notifyType: "notification", label: "Note", icon: StickyNote, stream: "notification", categoryKey: "nudges", categoryGroup: "alert",
    classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.NOTE },
    getTitle: (n) => defaultAlertTitle(n, "Note"),
    getDescription: (n) => defaultAlertDescription(n, "Note"),
    getStatusLabel: () => "Nudges",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open note", icon: StickyNote, openTarget: (notification) => ({ kind: "note", noteId: notification.note ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "note", noteId: n.note ?? undefined })); },
  },
  {
    type: ALERT_TYPES.FILE, notifyType: "notification", label: "File", icon: FileText, stream: "notification", categoryKey: "files", categoryGroup: "alert",
    classes: { bg: "bg-stone-500", text: "text-white", ring: "ring-stone-500/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.FILE },
    getTitle: (n) => defaultAlertTitle(n, "File"),
    getDescription: (n) => defaultAlertDescription(n, "File"),
    getStatusLabel: () => "Files",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open file", icon: FileText, openTarget: (notification) => ({ kind: "file", fileId: notification.file ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "file", fileId: n.file ?? undefined })); },
  },
  {
    type: ALERT_TYPES.LEADS, notifyType: "notification", label: "Leads", icon: Target, stream: "notification", categoryKey: "leads", categoryGroup: "alert",
    classes: { bg: "bg-success", text: "text-success-foreground", ring: "ring-success/20" },
    filter: { notify_type: "notification", type: ALERT_TYPES.LEADS },
    getTitle: (n) => defaultAlertTitle(n, "Leads"),
    getDescription: (n) => defaultAlertDescription(n, "Leads"),
    getStatusLabel: () => "Leads",
    getActorName: actorName,
    notableWhen: (n) => !n.is_seen || n.status === "Failed",
    aggregateKey: (n) => `notification:${n.alert_type}`,
    getActions: (n, ctx) => alertGetActions(n, ctx, { primaryActionLabel: "Open lead", icon: Target, openTarget: (notification) => ({ kind: "lead", leadId: notification.data_type ?? undefined }) }),
    onOpen: (n, ctx) => { runOpenAction(n, ctx, () => ctx.openNotification?.(n, { kind: "lead", leadId: n.data_type ?? undefined })); },
  },
];

// ─── Fallback Entry ────────────────────────────────────────────────────────────

export const FALLBACK_ENTRY: NotificationTypeEntry = {
  notifyType: "notification",
  type: -1,
  label: "Update",
  icon: AlertTriangle,
  stream: "notification",
  categoryKey: "general",
  categoryGroup: "alert",
  classes: { bg: "bg-muted", text: "text-muted-foreground", ring: "ring-muted/20" },
  filter: { notify_type: "notification", type: -1 as NotificationFilterType },
  getTitle: (n) => n.title?.trim() || n.alert_tag?.trim() || "Update",
  getDescription: (n) => n.description?.trim() || "Notification details unavailable",
  getStatusLabel: (n) => n.async_task?.status ?? n.status ?? "Update",
  getActorName: actorName,
  notableWhen: (n) => n.status === "Failed" || !n.is_seen,
  aggregateKey: (n) => `unknown:${n.async_task?.type ?? n.alert_type ?? "type"}`,
  getActions: () => [],
  onOpen: () => {},
};

// ─── Lookup Helpers ────────────────────────────────────────────────────────────

export function getNotificationConfig(notification: Notification): NotificationTypeEntry {
  const entry = notification.async_task != null
    ? NOTIFICATION_CONFIG.find((e) => e.notifyType === "async" && e.type === notification.async_task!.type)
    : NOTIFICATION_CONFIG.find((e) => e.notifyType === "notification" && e.type === notification.alert_type);
  return entry ?? { ...FALLBACK_ENTRY, type: notification.async_task?.type ?? notification.alert_type };
}

export function getAsyncNotificationConfig(
  type: AsyncTaskTypeId,
): NotificationTypeEntry & { getPendingMessage: (task: AsyncTask) => string } {
  const entry = NOTIFICATION_CONFIG.find((e) => e.notifyType === "async" && e.type === type);
  if (entry) return entry as NotificationTypeEntry & { getPendingMessage: (task: AsyncTask) => string };
  return {
    ...FALLBACK_ENTRY,
    notifyType: "async",
    type,
    filter: { notify_type: "async", type },
    stream: "system",
    getPendingMessage: () => "Working…",
    getCompletedMessage: () => "Complete",
    getResolvedMessage: () => "Resolved",
    getFailedMessage: () => "Failed",
  } as NotificationTypeEntry & { getPendingMessage: (task: AsyncTask) => string };
}

// ─── Category Metadata ─────────────────────────────────────────────────────────

interface CategoryMeta {
  key: string;
  group: NotificationCategoryGroup;
  label: string;
  caption: string;
  clauseSingular: string;
  clausePlural: string;
  icon: LucideIcon;
  classes: { bg: string; text: string; ring: string };
  order: number;
  accountAvailable: boolean;
}

const CATEGORIES: CategoryMeta[] = [
  { key: "marketing", group: "task", label: "Marketing", caption: "Marketing activity", clauseSingular: "marketing update", clausePlural: "marketing updates", icon: Globe, classes: { bg: "bg-plum", text: "text-plum-foreground", ring: "ring-plum/20" }, order: 10, accountAvailable: false },
  { key: "incentive", group: "task", label: "Incentive", caption: "Incentive evaluations", clauseSingular: "incentive update", clausePlural: "incentive updates", icon: Trophy, classes: { bg: "bg-ochre", text: "text-ochre-foreground", ring: "ring-ochre/20" }, order: 20, accountAvailable: false },
  { key: "imports", group: "task", label: "Import", caption: "Imports and exports", clauseSingular: "import update", clausePlural: "import updates", icon: FileUp, classes: { bg: "bg-cobalt", text: "text-cobalt-foreground", ring: "ring-cobalt/20" }, order: 30, accountAvailable: true },
  { key: "context", group: "task", label: "Context", caption: "Board context activity", clauseSingular: "context update", clausePlural: "context updates", icon: Layers, classes: { bg: "bg-ai", text: "text-ai-foreground", ring: "ring-ai/20" }, order: 40, accountAvailable: false },
  { key: "copy", group: "task", label: "Copy", caption: "Bulk copy operations", clauseSingular: "copy update", clausePlural: "copy updates", icon: Copy, classes: { bg: "bg-clay", text: "text-clay-foreground", ring: "ring-clay/20" }, order: 50, accountAvailable: true },
  { key: "board", group: "task", label: "Board", caption: "Board operations", clauseSingular: "board update", clausePlural: "board updates", icon: LayoutGrid, classes: { bg: "bg-action", text: "text-action-foreground", ring: "ring-action/20" }, order: 60, accountAvailable: true },
  { key: "reminders", group: "alert", label: "Reminders", caption: "Reminders", clauseSingular: "reminder", clausePlural: "reminders", icon: Bell, classes: { bg: "bg-warning", text: "text-warning-foreground", ring: "ring-warning/20" }, order: 70, accountAvailable: false },
  { key: "schedule", group: "alert", label: "Schedule", caption: "Schedule notifications", clauseSingular: "schedule notification", clausePlural: "schedule notifications", icon: CalendarClock, classes: { bg: "bg-rose", text: "text-rose-foreground", ring: "ring-rose/20" }, order: 80, accountAvailable: false },
  { key: "nudges", group: "alert", label: "Nudges", caption: "Nudges, checklists, and notes", clauseSingular: "nudge", clausePlural: "nudges", icon: ListChecks, classes: { bg: "bg-moss", text: "text-moss-foreground", ring: "ring-moss/20" }, order: 90, accountAvailable: false },
  { key: "board-alert", group: "alert", label: "Board alert", caption: "Board alerts", clauseSingular: "board alert", clausePlural: "board alerts", icon: ShieldAlert, classes: { bg: "bg-destructive", text: "text-destructive-foreground", ring: "ring-destructive/20" }, order: 100, accountAvailable: false },
  { key: "status", group: "alert", label: "Status", caption: "Entity Status changes", clauseSingular: "Status change", clausePlural: "Status changes", icon: Flag, classes: { bg: "bg-slate", text: "text-slate-foreground", ring: "ring-slate/20" }, order: 110, accountAvailable: false },
  { key: "general", group: "alert", label: "General", caption: "General updates", clauseSingular: "general update", clausePlural: "general updates", icon: Megaphone, classes: { bg: "bg-chart-3", text: "text-white", ring: "ring-chart-3/20" }, order: 120, accountAvailable: false },
  { key: "files", group: "alert", label: "Files", caption: "File activity", clauseSingular: "file update", clausePlural: "file updates", icon: FileText, classes: { bg: "bg-stone-500", text: "text-white", ring: "ring-stone-500/20" }, order: 130, accountAvailable: false },
  { key: "leads", group: "alert", label: "Leads", caption: "Lead activity", clauseSingular: "Lead", clausePlural: "Leads", icon: Target, classes: { bg: "bg-success", text: "text-success-foreground", ring: "ring-success/20" }, order: 140, accountAvailable: false },
];

export function getNotificationCategory(key: string): CategoryMeta | undefined {
  return CATEGORIES.find((c) => c.key === key);
}

// ─── Filter Helpers ────────────────────────────────────────────────────────────

export function getNotificationFilterOptions(
  options: { accountView?: boolean; group?: NotificationCategoryGroup } = {},
): NotificationFilterOption[] {
  return CATEGORIES
    .filter((c) => !options.accountView || c.accountAvailable)
    .filter((c) => !options.group || c.group === options.group)
    .sort((a, b) => a.order - b.order)
    .map((c) => ({
      value: c.key,
      label: c.label,
      icon: c.icon,
      group: c.group,
      classes: c.classes,
      filters: NOTIFICATION_CONFIG.filter((e) => e.categoryKey === c.key).map((e) => e.filter),
    }));
}

export function getNotificationSidebarOptions(
  accountView = false,
): Array<{ value: string; label: string; icon: LucideIcon; group: string }> {
  return [
    { value: "all", label: "All", icon: Inbox, group: "browse" },
    { value: "in-progress", label: "In progress", icon: Loader2, group: "status" },
    { value: "failed", label: "Failed", icon: AlertTriangle, group: "status" },
    { value: "completed", label: "Completed", icon: CheckCircle2, group: "status" },
    { value: "cancelled", label: "Cancelled", icon: Ban, group: "status" },
    ...getNotificationFilterOptions({ accountView }).map((c) => ({
      value: c.value,
      label: c.label,
      icon: c.icon,
      group: c.group,
    })),
  ];
}

export function toApiParams(
  filter: NotificationFilter,
): Pick<
  NotificationListParams,
  "category" | "notable" | "async_status" | "notification_status" | "is_seen"
> {
  switch (filter.kind) {
    case "all": return {};
    case "unread": return { is_seen: false };
    case "notable": return { notable: true };
    case "ids": return {};
    case "status":
      switch (filter.status) {
        case "in-progress": return { async_status: ["Pending"] };
        case "failed": return { async_status: ["Failed"], notification_status: ["Failed"] };
        case "completed": return { async_status: ["Completed", "Resolved"] };
        case "cancelled": return { async_status: ["Cancelled"] };
      }
      break;
    case "category":
      return { category: toApiCategoryKey(filter.category) };
    case "multi": {
      const result: Pick<NotificationListParams, "category" | "async_status" | "notification_status"> = {};
      if (filter.categories.length > 0) {
        result.category = filter.categories.map(toApiCategoryKey);
      }
      if (filter.statuses.length > 0) {
        const asyncStatuses: string[] = [];
        const notifStatuses: string[] = [];
        for (const status of filter.statuses) {
          switch (status) {
            case "in-progress": asyncStatuses.push("Pending"); break;
            case "failed": asyncStatuses.push("Failed"); notifStatuses.push("Failed"); break;
            case "completed": asyncStatuses.push("Completed", "Resolved"); break;
            case "cancelled": asyncStatuses.push("Cancelled"); break;
          }
        }
        if (asyncStatuses.length > 0) result.async_status = asyncStatuses;
        if (notifStatuses.length > 0) result.notification_status = notifStatuses;
      }
      return result;
    }
  }
  return {};
}

// ─── Count Mapping ─────────────────────────────────────────────────────────────

function rawStatusCount(
  response: NotificationCountsResponse,
  notifyType: NotificationNotifyType,
  statuses: readonly string[],
): number {
  return response.status_counts
    .filter((item) => item.notify_type === notifyType && statuses.includes(item.status))
    .reduce((total, item) => total + item.count, 0);
}

export function mapNotificationCounts(
  countResponse: NotificationCountsResponse,
): MappedNotificationCounts {
  const byCategory: Record<string, number> = Object.fromEntries(
    CATEGORIES.map((c) => [c.key, 0]),
  );

  if (countResponse.category_counts) {
    for (const raw of countResponse.category_counts) {
      const key = toUiCategoryKey(raw.category);
      byCategory[key] = (byCategory[key] ?? 0) + raw.count;
    }
  } else {
    for (const raw of countResponse.type_counts) {
      const entry = NOTIFICATION_CONFIG.find(
        (e) => e.notifyType === raw.notify_type && e.type === raw.type,
      );
      if (entry) byCategory[entry.categoryKey] = (byCategory[entry.categoryKey] ?? 0) + raw.count;
    }
  }

  const byStatus = {
    "in-progress": rawStatusCount(countResponse, "async", ["Pending"]),
    failed:
      rawStatusCount(countResponse, "async", ["Failed"]) +
      rawStatusCount(countResponse, "notification", ["Failed"]),
    completed: rawStatusCount(countResponse, "async", ["Completed", "Resolved"]),
    cancelled: rawStatusCount(countResponse, "async", ["Cancelled"]),
  };

  const statuses: NotificationMappedStatusCount[] = [
    { key: "in-progress", label: "In progress", count: byStatus["in-progress"], icon: Loader2, classes: { bg: "bg-amber-500", text: "text-white", ring: "ring-amber-500/20" }, filter: { kind: "status", status: "in-progress" }, asyncStatuses: ["Pending"], notificationStatuses: [] },
    { key: "failed", label: "Failed", count: byStatus.failed, icon: AlertTriangle, classes: { bg: "bg-red-500", text: "text-white", ring: "ring-red-500/20" }, filter: { kind: "status", status: "failed" }, asyncStatuses: ["Failed"], notificationStatuses: ["Failed"] },
    { key: "completed", label: "Completed", count: byStatus.completed, icon: CheckCircle2, classes: { bg: "bg-emerald-600", text: "text-white", ring: "ring-emerald-600/20" }, filter: { kind: "status", status: "completed" }, asyncStatuses: ["Completed", "Resolved"], notificationStatuses: [] },
    { key: "cancelled", label: "Cancelled", count: byStatus.cancelled, icon: Ban, classes: { bg: "bg-slate-400", text: "text-white", ring: "ring-slate-400/20" }, filter: { kind: "all" }, asyncStatuses: ["Cancelled"], notificationStatuses: [] },
  ];

  const categories: NotificationMappedCategoryCount[] = getNotificationFilterOptions().map((option) => ({
    ...option,
    count: byCategory[option.value] ?? 0,
    filter: { kind: "category" as const, category: option.value },
  }));

  return {
    total: countResponse.total_count,
    unread: countResponse.unread_count ?? 0,
    notable: countResponse.notable_count ?? 0,
    categories,
    statuses,
    byCategory,
    byStatus,
  };
}

// ─── Status Icons (for external consumers) ─────────────────────────────────────

export const NOTIFICATION_STATUS_ICONS = {
  "in-progress": Loader2,
  failed: AlertTriangle,
  completed: CheckCircle2,
  cancelled: Ban,
} as const;

export const NOTIFICATION_GENERIC_ICONS = {
  fallback: AlertTriangle,
  completed: CheckCircle2,
  open: ArrowUpRight,
  download: FileDown,
} as const;
